import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import DashboardShell from "@/components/DashboardShell";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, KeyRound, Check } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";

function formatApiError(detail, fallback = "Something went wrong.") {
  if (!detail) return fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return detail?.msg || JSON.stringify(detail);
}

export default function ChangePassword() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ current: "", next: "", confirm: "" });
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState(null);
  const [success, setSuccess] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr(null);
    if (form.next !== form.confirm) {
      setErr("New password and confirmation do not match.");
      return;
    }
    if (form.next.length < 8) {
      setErr("New password must be at least 8 characters.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/auth/change-password", {
        current_password: form.current,
        new_password: form.next,
      });
      setSuccess(true);
      toast.success("Password changed. Please log in again.");
      setTimeout(async () => {
        await logout();
        navigate("/login");
      }, 1500);
    } catch (e) {
      setErr(formatApiError(e?.response?.data?.detail, "Could not change password."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <DashboardShell>
      <div data-testid="change-password-root">
        <div className="overline text-sg-gold-deep">SECURITY</div>
        <h1 className="font-display text-4xl text-sg-navy mt-1">Change <span className="font-serif-italic text-sg-gold-deep">password</span></h1>
        <p className="text-sg-mute mt-2 max-w-2xl">You'll be signed out on success — sign in with your new password.</p>

        <form onSubmit={onSubmit} className="mt-8 bg-white border border-sg-line rounded-2xl p-7 max-w-md space-y-4" data-testid="change-password-form">
          <div>
            <Label htmlFor="cp-current" className="text-xs uppercase tracking-wider text-sg-mute">Current password</Label>
            <Input id="cp-current" type="password" required value={form.current} onChange={(e) => setForm({ ...form, current: e.target.value })}
                   className="mt-1 bg-sg-cream-soft h-11" autoComplete="current-password" data-testid="cp-current" />
          </div>
          <div>
            <Label htmlFor="cp-next" className="text-xs uppercase tracking-wider text-sg-mute">New password (8+ chars)</Label>
            <Input id="cp-next" type="password" required minLength={8} value={form.next} onChange={(e) => setForm({ ...form, next: e.target.value })}
                   className="mt-1 bg-sg-cream-soft h-11" autoComplete="new-password" data-testid="cp-next" />
          </div>
          <div>
            <Label htmlFor="cp-confirm" className="text-xs uppercase tracking-wider text-sg-mute">Confirm new password</Label>
            <Input id="cp-confirm" type="password" required minLength={8} value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })}
                   className="mt-1 bg-sg-cream-soft h-11" autoComplete="new-password" data-testid="cp-confirm" />
          </div>
          {err && (
            <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3" data-testid="cp-error">{err}</div>
          )}
          {success ? (
            <div className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg p-3 inline-flex items-center gap-2" data-testid="cp-success">
              <Check className="w-4 h-4" /> Password changed. Redirecting…
            </div>
          ) : (
            <Button type="submit" disabled={submitting} size="lg" className="w-full rounded-full bg-sg-navy hover:bg-sg-navy-700 text-white h-12" data-testid="cp-submit">
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <KeyRound className="w-4 h-4 mr-2" />}
              Change password
            </Button>
          )}
        </form>
      </div>
    </DashboardShell>
  );
}
